/**
 * Public realtor portfolio page (Phase 4).
 *
 * Mounted at `/r/:slug`. Unauthenticated read of the realtor's hero,
 * active listings, and sold count. The backend at
 * `GET /api/v1/realtors/public/:slug` returns 404 for any unpublished /
 * unverified slug — same existence-leak posture as the public listing
 * surface.
 */

import type { TargetReviewsResponse } from "@shared/types";
import {
	ArrowLeft,
	BedDouble,
	ImageOff,
	Languages,
	MapPin,
	ShieldCheck,
	Sparkles,
	Star,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { ReviewSummarySection } from "@/components/ui/review-summary-section";
import { Skeleton } from "@/components/ui/skeleton";
import {
	getPublicPortfolio,
	type PortfolioPayload,
	type PublicPortfolioListing,
} from "@/features/realtor/services/public-portfolio-api.service";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";
import { resolveMediaUrl } from "@/lib/utils";
import { getRealtorReviews } from "@/services/reviews-api.service";

type LoadState =
	| { status: "loading" }
	| { status: "not-found" }
	| { status: "error"; message: string }
	| { status: "loaded"; portfolio: PortfolioPayload };

export function PublicPortfolioPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { slug } = useParams<{ slug: string }>();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [reviews, setReviews] = useState<TargetReviewsResponse>({
		summary: { averageRating: null, reviewCount: 0 },
		reviews: [],
	});

	const runLoad = useCallback(
		async (cancelledRef: { cancelled: boolean }) => {
			if (!slug) {
				if (!cancelledRef.cancelled) setLoad({ status: "not-found" });
				return;
			}
			setLoad({ status: "loading" });
			// Reset reviews to the empty default at the *start* of every
			// load, BEFORE the portfolio fetch resolves. Without this,
			// navigating between two realtor portfolios briefly renders
			// the previous realtor's review summary under the new
			// realtor's hero (the portfolio fetch lands first; reviews
			// follow asynchronously).
			setReviews({
				summary: { averageRating: null, reviewCount: 0 },
				reviews: [],
			});
			const result = await getPublicPortfolio(slug);
			if (cancelledRef.cancelled) return;
			if (!result.success) {
				if (result.status === "not-found") {
					setLoad({ status: "not-found" });
				} else {
					setLoad({ status: "error", message: result.message });
				}
				return;
			}
			setLoad({ status: "loaded", portfolio: result.portfolio });

			// Reviews fire after the portfolio resolves so the page can
			// render the hero/listings while reviews trickle in. The
			// service degrades to an empty payload on error, so a 4xx/5xx
			// here can't break the page.
			const reviewResult = await getRealtorReviews(result.portfolio.realtor.id);
			if (cancelledRef.cancelled) return;
			setReviews(reviewResult);
		},
		[slug],
	);

	useEffect(() => {
		const ref = { cancelled: false };
		runLoad(ref);
		return () => {
			ref.cancelled = true;
		};
	}, [runLoad]);

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen w-full py-12"
				data-testid="public-portfolio-loading"
			>
				<div className="max-w-5xl mx-auto px-6 space-y-6">
					<Skeleton className="h-48 w-full rounded-xl bg-dashboard-bg" />
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						<div className="lg:col-span-2 space-y-6">
							<Skeleton className="h-64 w-full rounded-xl bg-dashboard-bg" />
						</div>
						<div className="lg:col-span-1 space-y-6">
							<Skeleton className="h-48 w-full rounded-xl bg-dashboard-bg" />
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (load.status === "not-found") {
		return (
			<div className="bg-dashboard-surface min-h-screen w-full flex items-center justify-center p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)] max-w-md w-full">
					<CardContent className="p-6 text-center space-y-3">
						<h1
							className="text-lg font-semibold font-display italic"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.portfolio.notFoundTitle")}
						</h1>
						<p
							className="text-sm font-body"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("realtor.portfolio.notFoundBody")}
						</p>
						<StudioButton variant="secondary" onClick={() => navigate("/")}>
							{t("realtor.portfolio.backHome")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen w-full flex items-center justify-center p-6">
				<Card
					className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)] max-w-md w-full"
					style={{
						borderColor: "var(--color-dashboard-pill-risk)",
					}}
				>
					<CardContent className="p-6 text-center space-y-3" role="alert">
						<p
							className="text-sm font-body"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{load.message}
						</p>
						<StudioButton
							variant="secondary"
							onClick={() => runLoad({ cancelled: false })}
						>
							{t("realtor.portfolio.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { realtor, activeListings, soldCount } = load.portfolio;
	const isVerified = realtor.reraVerified && realtor.idVerified;

	return (
		<div className="bg-dashboard-surface min-h-screen w-full pb-12">
			{/* Main Content Container */}
			<div className="max-w-5xl mx-auto px-6 pt-12 space-y-8">
				{/* Profile Info Card */}
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)] overflow-hidden">
					<CardContent className="p-6 sm:p-8">
						<div className="flex flex-col md:flex-row items-center md:items-start text-center md:text-left gap-6">
							<Avatar
								className="h-28 w-28 border-4 shadow-lg shrink-0"
								style={{ borderColor: "var(--color-dashboard-bg)" }}
							>
								{realtor.profileImageUrl ? (
									<AvatarImage
										src={resolveMediaUrl(realtor.profileImageUrl)}
										alt={realtor.name}
									/>
								) : null}
								<AvatarFallback
									className="text-3xl font-semibold"
									style={{
										backgroundColor: "var(--color-dashboard-pill-awaiting-bg)",
										color: "var(--color-dashboard-pill-awaiting)",
									}}
								>
									{realtor.name.charAt(0).toUpperCase()}
								</AvatarFallback>
							</Avatar>

							<div className="space-y-4 flex-1">
								<div className="space-y-2">
									<p
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 10,
											fontWeight: 600,
											letterSpacing: "0.2em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-muted)",
										}}
									>
										{t("realtor.portfolio.verifiedAria")}
									</p>
									<div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 justify-center md:justify-start">
										<h1
											style={{
												fontFamily: "var(--font-display)",
												fontSize: 36,
												fontWeight: 500,
												fontStyle: "italic",
												color: "var(--color-dashboard-text)",
												lineHeight: 1.1,
											}}
										>
											{realtor.name}
										</h1>
										{isVerified && (
											<span
												className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium"
												style={{
													backgroundColor:
														"var(--color-dashboard-pill-done-bg)",
													color: "var(--color-dashboard-pill-done)",
												}}
											>
												<ShieldCheck className="h-3.5 w-3.5" />
												Verified Partner
											</span>
										)}
									</div>
									{realtor.agencyName && (
										<p
											className="text-sm font-medium"
											style={{ color: "var(--color-dashboard-muted)" }}
										>
											{realtor.agencyName}
										</p>
									)}
									{realtor.serviceCities.length > 0 && (
										<p
											className="text-xs inline-flex items-center gap-1 justify-center md:justify-start"
											style={{ color: "var(--color-dashboard-muted)" }}
										>
											<MapPin className="h-3.5 w-3.5 text-dashboard-accent-dark" />
											{realtor.serviceCities.join(" · ")}
										</p>
									)}
								</div>

								{realtor.bio && (
									<div className="border-t pt-4 border-dashboard-border">
										<p
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 14,
												lineHeight: 1.6,
												color: "var(--color-dashboard-muted)",
											}}
										>
											{realtor.bio}
										</p>
									</div>
								)}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Two-Column Details Grid */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Left Column: Listings & Reviews */}
					<div className="lg:col-span-2 space-y-8">
						{/* Active Listings */}
						<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)] p-6 space-y-6">
							<div className="flex items-center justify-between border-b pb-4 border-dashboard-border">
								<h2
									style={{
										fontFamily: "var(--font-display)",
										fontSize: 22,
										fontWeight: 500,
										fontStyle: "italic",
										color: "var(--color-dashboard-text)",
									}}
								>
									{t("realtor.portfolio.activeListingsTitle")}
								</h2>
								<span className="text-xs px-3 py-1 rounded-full font-mono bg-dashboard-surface text-dashboard-muted">
									{activeListings.length}{" "}
									{activeListings.length === 1 ? "Listing" : "Listings"}
								</span>
							</div>

							{activeListings.length === 0 ? (
								<div className="py-12 text-center space-y-3">
									<Sparkles
										className="h-6 w-6 mx-auto"
										style={{ color: "var(--color-dashboard-accent-dark)" }}
									/>
									<p
										className="text-sm font-body"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{t("realtor.portfolio.noActiveListings")}
									</p>
								</div>
							) : (
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{activeListings.map((listing) => (
										<ListingCard
											key={listing.id}
											listing={listing}
											onClick={
												listing.publicSlug
													? () => navigate(`/listings/${listing.publicSlug}`)
													: undefined
											}
										/>
									))}
								</div>
							)}
						</Card>

						{/* Reviews */}
						{reviews.summary.reviewCount > 0 && (
							<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)] p-6">
								<ReviewSummarySection
									title={t("realtor.portfolio.reviewsTitle")}
									summary={reviews.summary}
									reviews={reviews.reviews}
									emptyMessage={t("realtor.portfolio.reviewsEmpty")}
								/>
							</Card>
						)}
					</div>

					{/* Right Column: Credentials & Tags */}
					<div className="lg:col-span-1 space-y-8">
						{/* Trust & Stats Card */}
						<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)] p-6 space-y-6">
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontSize: 18,
									fontWeight: 500,
									fontStyle: "italic",
									color: "var(--color-dashboard-text)",
								}}
							>
								Credentials & Trust
							</h3>

							{/* High-contrast Stats Grid */}
							<div className="grid grid-cols-2 gap-4">
								<div className="bg-dashboard-surface border border-dashboard-border rounded-lg p-4 text-center space-y-1">
									<span
										style={{
											fontFamily: "var(--font-display)",
											fontSize: 28,
											fontWeight: 500,
											color: "var(--color-dashboard-accent-dark)",
										}}
									>
										{activeListings.length}
									</span>
									<p className="text-[10px] font-mono tracking-wider text-dashboard-muted uppercase">
										{t("realtor.portfolio.activeListings")}
									</p>
								</div>
								<div className="bg-dashboard-surface border border-dashboard-border rounded-lg p-4 text-center space-y-1">
									<span
										style={{
											fontFamily: "var(--font-display)",
											fontSize: 28,
											fontWeight: 500,
											color: "var(--color-dashboard-accent-dark)",
										}}
									>
										{soldCount}
									</span>
									<p className="text-[10px] font-mono tracking-wider text-dashboard-muted uppercase">
										{t("realtor.portfolio.transactionsClosed")}
									</p>
								</div>
							</div>

							{/* Credentials List */}
							<div className="space-y-3 pt-2">
								{realtor.reraNumber && (
									<div className="flex items-center gap-3 text-sm">
										<ShieldCheck className="h-5 w-5 text-dashboard-accent-dark shrink-0" />
										<div>
											<p className="text-[10px] font-mono text-dashboard-muted uppercase">
												RERA Number
											</p>
											<p className="font-medium text-dashboard-text">
												RERA · {realtor.reraNumber}
											</p>
										</div>
									</div>
								)}
								{typeof realtor.yearsExperience === "number" && (
									<div className="flex items-center gap-3 text-sm">
										<Star className="h-5 w-5 text-dashboard-accent-dark shrink-0" />
										<div>
											<p className="text-[10px] font-mono text-dashboard-muted uppercase">
												Experience
											</p>
											<p className="font-medium text-dashboard-text">
												{t("realtor.portfolio.yearsExperience", {
													count: realtor.yearsExperience,
												})}
											</p>
										</div>
									</div>
								)}
								{realtor.languages.length > 0 && (
									<div className="flex items-center gap-3 text-sm">
										<Languages className="h-5 w-5 text-dashboard-accent-dark shrink-0" />
										<div>
											<p className="text-[10px] font-mono text-dashboard-muted uppercase">
												Languages
											</p>
											<p className="font-medium text-dashboard-text">
												{realtor.languages.join(", ")}
											</p>
										</div>
									</div>
								)}
							</div>
						</Card>

						{/* Specializations & Areas Card */}
						{(realtor.specializations.length > 0 ||
							realtor.serviceLocalities.length > 0) && (
							<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)] p-6 space-y-6">
								{realtor.specializations.length > 0 && (
									<TagBlock
										title={t("realtor.portfolio.specializations")}
										tags={realtor.specializations}
									/>
								)}
								{realtor.serviceLocalities.length > 0 && (
									<TagBlock
										title={t("realtor.portfolio.serviceLocalities")}
										tags={realtor.serviceLocalities}
									/>
								)}
							</Card>
						)}
					</div>
				</div>

				{/* Back navigation */}
				<div className="pt-4">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="inline-flex items-center gap-2 text-sm font-medium hover:text-dashboard-accent-dark transition-colors"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						<ArrowLeft className="h-4 w-4" />
						{t("realtor.portfolio.back")}
					</button>
				</div>
			</div>
		</div>
	);
}

function ListingCard({
	listing,
	onClick,
}: {
	listing: PublicPortfolioListing;
	onClick?: () => void;
}) {
	const cover = listing.images[0];
	return (
		<button
			type="button"
			onClick={onClick}
			className="group text-left rounded-xl border overflow-hidden hover:shadow-md transition-all duration-300 w-full"
			disabled={!onClick}
			style={{
				backgroundColor: "var(--color-dashboard-paper)",
				borderColor: "var(--color-dashboard-border)",
			}}
		>
			<div
				className="aspect-16/10 w-full flex items-center justify-center overflow-hidden relative"
				style={{ backgroundColor: "var(--color-dashboard-surface)" }}
			>
				{cover ? (
					<img
						src={resolveMediaUrl(cover)}
						alt={listing.headline}
						className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
					/>
				) : (
					<ImageOff
						className="h-8 w-8"
						style={{ color: "var(--color-dashboard-muted)" }}
					/>
				)}
			</div>
			<div className="p-4 space-y-3">
				<div className="space-y-1">
					<p
						className="text-sm font-medium font-body line-clamp-1"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{listing.headline}
					</p>
					<p
						className="text-xs inline-flex items-center gap-1.5"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{listing.bhk != null && (
							<span className="inline-flex items-center gap-0.5 text-dashboard-accent-dark">
								<BedDouble className="h-3 w-3" />
								{listing.bhk} BHK
							</span>
						)}
						{listing.bhk != null && <span>·</span>}
						<span className="truncate">
							{listing.locality}, {listing.city}
						</span>
					</p>
				</div>
				<div className="flex items-center justify-between border-t pt-3 border-dashboard-border">
					<p
						className="text-sm font-semibold"
						style={{ color: "var(--color-dashboard-accent-dark)" }}
					>
						{formatPaiseAsRupees(listing.askingPrice)}
					</p>
					{listing.publicSlug && (
						<span className="text-[10px] font-mono tracking-wider uppercase text-dashboard-accent-dark opacity-0 group-hover:opacity-100 transition-opacity">
							View Details &rarr;
						</span>
					)}
				</div>
			</div>
		</button>
	);
}

function TagBlock({ title, tags }: { title: string; tags: string[] }) {
	return (
		<div className="space-y-2">
			<h4
				className="text-[10px] font-mono tracking-wider uppercase"
				style={{ color: "var(--color-dashboard-muted)" }}
			>
				{title}
			</h4>
			<div className="flex flex-wrap gap-2">
				{tags.map((tag) => (
					<span
						key={tag}
						className="inline-flex items-center rounded-full px-3 py-1 text-xs border"
						style={{
							backgroundColor: "var(--color-dashboard-surface)",
							borderColor: "var(--color-dashboard-border)",
							color: "var(--color-dashboard-text)",
						}}
					>
						{tag}
					</span>
				))}
			</div>
		</div>
	);
}
